import React, { useState } from 'react';
import { X, AlertCircle, Loader2, Calendar, Clock, MapPin, Users, CreditCard, ArrowRight, ArrowLeft, CheckCircle } from 'lucide-react';
import { ITALIAN_PROVINCES } from '../constants/cities';

interface EventData {
  id: string;
  name: string;
  date: string;
  time: string;
  location: string;
  city: string;
  age_range: string;
  men_price: number;
  women_price: number;
  max_participants: number;
  participant_count: number | null;
}

interface EventRegistrationModalProps {
  isOpen: boolean;
  onClose: () => void;
  event: EventData | null;
}

type Step = 'form' | 'confirm' | 'success';

const EventRegistrationModal: React.FC<EventRegistrationModalProps> = ({ isOpen, onClose, event }) => {
  const [step, setStep] = useState<Step>('form');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    gender: '',
    birthdate: '',
    city: 'ROMA'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    setFormData({ name: '', email: '', phone: '', gender: '', birthdate: '', city: 'ROMA' });
    setStep('form');
    setError(null);
    setLoading(false);
    onClose();
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('it-IT', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const getPrice = () => {
    if (!event) return 0;
    const isWoman = formData.gender === 'donna_etero' || formData.gender === 'donna_gay';
    return isWoman ? Number(event.women_price) : Number(event.men_price);
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setStep('confirm');
  };

  const handlePayment = async () => {
    if (!event) return;
    setLoading(true);
    setError(null);

    try {
      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/event-register`;
      const currentUrl = window.location.origin;

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          event_id: event.id,
          ...formData,
          name: formData.name.toUpperCase(),
          success_url: `${currentUrl}?registration=success&event=${encodeURIComponent(event.name)}`,
          cancel_url: `${currentUrl}?registration=cancelled`,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Errore durante la registrazione');
      }

      if (data.free) {
        setStep('success');
      } else if (data.url) {
        window.location.href = data.url;
      }
    } catch (err: any) {
      setError(err.message || 'Errore durante la registrazione. Riprova.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !event) return null;

  const price = getPrice();
  const availableSpots = event.max_participants - (event.participant_count || 0);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
        <div className="bg-gradient-to-r from-[#D91C5C] to-[#8B1538] p-5 flex justify-between items-center flex-shrink-0">
          <div>
            <h2 className="text-xl font-bold text-white">
              {step === 'form' && 'Registrati all\'Evento'}
              {step === 'confirm' && 'Conferma Registrazione'}
              {step === 'success' && 'Registrazione Completata'}
            </h2>
            <p className="text-white/80 text-sm mt-0.5">{event.name}</p>
          </div>
          <button onClick={handleClose} className="text-white/80 hover:text-white transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1">
          {step === 'form' && (
            <div className="p-6">
              <div className="bg-gray-50 rounded-xl p-4 mb-6 space-y-2">
                <div className="flex items-center gap-2 text-gray-700 text-sm">
                  <Calendar className="h-4 w-4 text-[#D91C5C]" />
                  <span>{formatDate(event.date)}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-700 text-sm">
                  <Clock className="h-4 w-4 text-[#D91C5C]" />
                  <span>{event.time}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-700 text-sm">
                  <MapPin className="h-4 w-4 text-[#D91C5C]" />
                  <span>{event.location}, {event.city}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-700 text-sm">
                  <Users className="h-4 w-4 text-[#D91C5C]" />
                  <span>{availableSpots} posti rimanenti</span>
                </div>
              </div>

              {error && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm mb-4">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <form onSubmit={handleFormSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nome Completo</label>
                  <input
                    type="text"
                    required
                    className="w-full rounded-lg border-gray-300 shadow-sm focus:border-[#D91C5C] focus:ring-[#D91C5C] uppercase"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value.toUpperCase() })}
                    placeholder="Es. Mario Rossi"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    required
                    className="w-full rounded-lg border-gray-300 shadow-sm focus:border-[#D91C5C] focus:ring-[#D91C5C]"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="Es. mario@email.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Telefono</label>
                  <input
                    type="tel"
                    required
                    className="w-full rounded-lg border-gray-300 shadow-sm focus:border-[#D91C5C] focus:ring-[#D91C5C]"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="Es. +39 333 1234567"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sesso</label>
                  <select
                    required
                    className="w-full rounded-lg border-gray-300 shadow-sm focus:border-[#D91C5C] focus:ring-[#D91C5C]"
                    value={formData.gender}
                    onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                  >
                    <option value="">Seleziona sesso</option>
                    <option value="uomo_etero">Uomo Etero</option>
                    <option value="donna_etero">Donna Etero</option>
                    <option value="uomo_gay">Uomo Gay</option>
                    <option value="donna_gay">Donna Gay</option>
                    <option value="non_dichiarato">Non dichiarato</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Data di Nascita</label>
                  <input
                    type="date"
                    required
                    max={new Date().toISOString().split('T')[0]}
                    className="w-full rounded-lg border-gray-300 shadow-sm focus:border-[#D91C5C] focus:ring-[#D91C5C]"
                    value={formData.birthdate}
                    onChange={(e) => setFormData({ ...formData, birthdate: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Citta</label>
                  <select
                    required
                    className="w-full rounded-lg border-gray-300 shadow-sm focus:border-[#D91C5C] focus:ring-[#D91C5C]"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  >
                    {ITALIAN_PROVINCES.map((city) => (
                      <option key={city} value={city}>{city}</option>
                    ))}
                  </select>
                </div>

                <button
                  type="submit"
                  className="w-full bg-[#D91C5C] hover:bg-[#C01850] text-white py-3 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 mt-2"
                >
                  Continua
                  <ArrowRight className="h-5 w-5" />
                </button>
              </form>
            </div>
          )}

          {step === 'confirm' && (
            <div className="p-6">
              {error && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm mb-4">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <div className="bg-gray-50 rounded-xl p-5 mb-6">
                <h3 className="font-bold text-gray-900 mb-3 text-lg">{event.name}</h3>
                <div className="space-y-2 text-sm text-gray-700">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-[#D91C5C]" />
                    <span>{formatDate(event.date)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-[#D91C5C]" />
                    <span>{event.time}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-[#D91C5C]" />
                    <span>{event.location}, {event.city}</span>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-xl p-5 mb-6">
                <h4 className="font-semibold text-gray-900 mb-3">I Tuoi Dati</h4>
                <div className="space-y-1.5 text-sm text-gray-700">
                  <p><span className="text-gray-500">Nome:</span> {formData.name.toUpperCase()}</p>
                  <p><span className="text-gray-500">Email:</span> {formData.email}</p>
                  <p><span className="text-gray-500">Telefono:</span> {formData.phone}</p>
                  <p><span className="text-gray-500">Sesso:</span> {formData.gender.replace('_', ' ')}</p>
                  <p><span className="text-gray-500">Data di nascita:</span> {new Date(formData.birthdate).toLocaleDateString('it-IT')}</p>
                  <p><span className="text-gray-500">Citta:</span> {formData.city}</p>
                </div>
              </div>

              <div className="bg-[#FFF5F7] border border-[#D91C5C]/20 rounded-xl p-5 mb-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CreditCard className="h-5 w-5 text-[#D91C5C]" />
                    <span className="font-semibold text-gray-900">Totale da pagare</span>
                  </div>
                  <span className="text-2xl font-bold text-[#D91C5C]">
                    {price === 0 ? 'Gratuito' : `${price.toFixed(2)} EUR`}
                  </span>
                </div>
                {price > 0 && (
                  <p className="text-xs text-gray-500 mt-2">
                    Verrai reindirizzato alla pagina di pagamento sicuro Stripe
                  </p>
                )}
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => { setStep('form'); setError(null); }}
                  disabled={loading}
                  className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-3 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  <ArrowLeft className="h-5 w-5" />
                  Modifica
                </button>
                <button
                  onClick={handlePayment}
                  disabled={loading}
                  className="flex-1 bg-[#D91C5C] hover:bg-[#C01850] text-white py-3 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      Elaborazione...
                    </>
                  ) : price === 0 ? (
                    <>
                      Conferma
                      <CheckCircle className="h-5 w-5" />
                    </>
                  ) : (
                    <>
                      Paga {price.toFixed(2)} EUR
                      <CreditCard className="h-5 w-5" />
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {step === 'success' && (
            <div className="p-6 text-center py-10">
              <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">Registrazione completata!</h3>
              <p className="text-gray-600 mb-6">
                Sei stato registrato con successo all'evento <strong>{event.name}</strong>.
                Riceverai una conferma via email.
              </p>
              <button
                onClick={handleClose}
                className="bg-[#D91C5C] hover:bg-[#C01850] text-white px-8 py-2.5 rounded-lg font-semibold transition-all"
              >
                Chiudi
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EventRegistrationModal;
